// ============================================================================
// AI Report Scope — report-specific/default evidence filters + instructions
// ============================================================================

const STORAGE_PREFIX = 'metricus.aiReportScope';

export const MIN_AI_SAMPLE_ITEM_LIMIT = 1;
export const DEFAULT_AI_SAMPLE_ITEM_LIMIT = 100;
export const MAX_AI_SAMPLE_ITEM_LIMIT = 1000;

export const DEFAULT_AI_REPORT_SCOPE = {
  enabled: false,
  useCurrentDatasetFilters: true,
  instructions: '',
  sampleItemLimit: DEFAULT_AI_SAMPLE_ITEM_LIMIT,
  excludeVariantIds: [],
  excludeWorkItems: [],
  excludePaths: [],
  excludeFieldValues: [],
};

const safeArray = (value) => Array.isArray(value) ? value : [];

export const normalizeSampleItemLimit = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return DEFAULT_AI_SAMPLE_ITEM_LIMIT;
  return Math.max(MIN_AI_SAMPLE_ITEM_LIMIT, Math.min(MAX_AI_SAMPLE_ITEM_LIMIT, Math.round(n)));
};

export const hasMeaningfulAiReportScope = (scope = {}) => {
  const s = (scope && typeof scope === 'object') ? scope : {};
  return Boolean(
    String(s.instructions || '').trim() ||
    safeArray(s.excludeVariantIds).length ||
    safeArray(s.excludeWorkItems).length ||
    safeArray(s.excludePaths).length ||
    safeArray(s.excludeFieldValues).length ||
    normalizeSampleItemLimit(s.sampleItemLimit ?? s.sampleLimit ?? DEFAULT_AI_SAMPLE_ITEM_LIMIT) !== DEFAULT_AI_SAMPLE_ITEM_LIMIT
  );
};

export const normalizeAiReportScope = (scope = {}) => {
  const raw = (scope && typeof scope === 'object') ? scope : {};
  const enabled = typeof raw.enabled === 'boolean'
    ? raw.enabled
    : hasMeaningfulAiReportScope(raw);

  return {
    ...DEFAULT_AI_REPORT_SCOPE,
    ...raw,
    enabled,
    sampleItemLimit: normalizeSampleItemLimit(raw?.sampleItemLimit ?? raw?.sampleLimit ?? DEFAULT_AI_SAMPLE_ITEM_LIMIT),
    excludeVariantIds: safeArray(raw?.excludeVariantIds).map(String).filter(Boolean),
    excludeWorkItems: safeArray(raw?.excludeWorkItems).map(String).map(v => v.trim()).filter(Boolean),
    excludePaths: safeArray(raw?.excludePaths)
      .map(p => ({ from: String(p?.from || '').trim(), to: String(p?.to || '').trim() }))
      .filter(p => p.from || p.to),
    excludeFieldValues: safeArray(raw?.excludeFieldValues)
      .map(r => ({
        field: String(r?.field || '').trim(),
        operator: String(r?.operator || 'equals').trim() || 'equals',
        value: String(r?.value || '').trim(),
      }))
      .filter(r => r.field),
  };
};

const storageKey = (modelId, reportKey) => `${STORAGE_PREFIX}.${modelId || 'unknown'}.${reportKey || 'default'}`;

export const loadAiReportScopeLocal = (modelId, reportKey) => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(storageKey(modelId, reportKey));
    return raw ? normalizeAiReportScope(JSON.parse(raw)) : null;
  } catch (_) {
    return null;
  }
};

export const saveAiReportScopeLocal = (modelId, reportKey, scope) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(storageKey(modelId, reportKey), JSON.stringify(normalizeAiReportScope(scope)));
  } catch (_) {}
};

export const loadAiReportScopeBackend = async (modelId, reportKey) => {
  if (!modelId || !reportKey) return null;
  try {
    const { invoke } = await import('@forge/bridge');
    const res = await invoke('getAIReportScope', { modelId, reportKey });
    if (!res?.success) return null;
    if (!res.scope) {
      return {
        scope: normalizeAiReportScope({}),
        source: 'none',
        reportScope: res.reportScope || null,
        globalScope: res.globalScope || null,
        allScopes: res.allScopes || null,
      };
    }
    const rawScope = typeof res.scope === 'string' ? JSON.parse(res.scope) : res.scope;
    return {
      scope: normalizeAiReportScope(rawScope),
      source: res.reportScope ? 'report' : (res.globalScope ? 'global' : 'model'),
      reportScope: res.reportScope || null,
      globalScope: res.globalScope || null,
      allScopes: res.allScopes || null,
    };
  } catch (_) {
    return null;
  }
};

export const saveAiReportScopeBackend = async (modelId, reportKey, scope, saveMode = 'report') => {
  if (!modelId || !reportKey) return { success: false, localOnly: true };
  try {
    const { invoke } = await import('@forge/bridge');
    return await invoke('saveAIReportScope', {
      modelId,
      reportKey,
      saveMode,
      scope: JSON.stringify(normalizeAiReportScope(scope)),
    });
  } catch (error) {
    return { success: false, localOnly: true, error: error?.message || String(error) };
  }
};

const norm = (value) => String(value ?? '').trim().toLowerCase();
const canon = (value) => norm(value).replace(/[\s_\-]+/g, '');

const getWorkItemValue = (row) => {
  if (!row || typeof row !== 'object') return '';
  const keys = Object.keys(row);
  const explicit = keys.find(k => ['work item #', 'task #', 'ticket #', 'issue #', 'key'].includes(norm(k)));
  if (explicit) return row[explicit];
  const hashKey = keys.find(k => norm(k).endsWith('#'));
  return hashKey ? row[hashKey] : (row.k || row.key || row.id || '');
};

const resolveRowField = (row, field) => {
  if (!row || !field) return undefined;
  if (Object.prototype.hasOwnProperty.call(row, field)) return row[field];
  const target = canon(field);
  const key = Object.keys(row).find(k => canon(k) === target);
  return key ? row[key] : undefined;
};

const matchesFieldRule = (row, rule) => {
  const v = resolveRowField(row, rule.field);
  const value = String(v ?? '').trim();
  const lhs = norm(value);
  const rhs = norm(rule.value);
  switch (rule.operator) {
    case 'not_equals': return lhs !== rhs;
    case 'contains': return lhs.includes(rhs);
    case 'not_contains': return !lhs.includes(rhs);
    case 'starts_with': return lhs.startsWith(rhs);
    case 'ends_with': return lhs.endsWith(rhs);
    case 'is_blank': return value === '' || value === 'N/A' || value === '—';
    case 'not_blank': return value !== '' && value !== 'N/A' && value !== '—';
    case 'equals':
    default: return lhs === rhs;
  }
};

const getVariantId = (row) => row?.['Variant ID'] ?? row?.variant_id ?? row?.Variant ?? row?.variant ?? row?.id;
const stepMatchesPath = (step, path) => {
  const from = step?.from_activity ?? step?.from ?? step?.['From Activity'] ?? '';
  const to = step?.to_activity ?? step?.to ?? step?.['To Activity'] ?? '';
  const fromOk = !path.from || canon(from) === canon(path.from);
  const toOk = !path.to || canon(to) === canon(path.to);
  return fromOk && toOk;
};

export const applyAiReportScopeToEvidence = ({ rawSampleItems = [], processSteps = [], variants = [], scope = {} } = {}) => {
  const s = normalizeAiReportScope(scope);
  const originalItems = safeArray(rawSampleItems).length;
  const sampleItemLimit = s.enabled ? normalizeSampleItemLimit(s.sampleItemLimit) : DEFAULT_AI_SAMPLE_ITEM_LIMIT;

  if (s.enabled === false) {
    const scopedItems = safeArray(rawSampleItems).slice(0, sampleItemLimit);
    return {
      rawSampleItems: scopedItems,
      processSteps,
      variants,
      summary: {
        enabled: false,
        originalItems,
        scopedItems: scopedItems.length,
        filteredItemsBeforeSampleLimit: originalItems,
        sampleItemLimit,
        sampleLimitedItems: Math.max(0, originalItems - scopedItems.length),
        excludedItems: 0,
        originalSteps: safeArray(processSteps).length,
        scopedSteps: safeArray(processSteps).length,
        excludedSteps: 0,
        originalVariants: safeArray(variants).length,
        scopedVariants: safeArray(variants).length,
        excludedVariantsFromEvidence: 0,
        filtersApplied: 0,
        notes: [],
        instructions: '',
        scope: s,
      },
    };
  }

  const variantSet = new Set(s.excludeVariantIds.map(String));
  const workItemSet = new Set(s.excludeWorkItems.map(v => norm(v)));

  let scopedItems = safeArray(rawSampleItems);

  if (variantSet.size > 0) {
    scopedItems = scopedItems.filter(row => !variantSet.has(String(getVariantId(row))));
  }
  if (workItemSet.size > 0) {
    scopedItems = scopedItems.filter(row => !workItemSet.has(norm(getWorkItemValue(row))));
  }
  if (s.excludeFieldValues.length > 0) {
    scopedItems = scopedItems.filter(row => !s.excludeFieldValues.some(rule => matchesFieldRule(row, rule)));
  }

  const filteredItems = scopedItems;
  const filteredItemsBeforeSampleLimit = filteredItems.length;
  scopedItems = filteredItems.slice(0, sampleItemLimit);

  const scopedSteps = safeArray(processSteps).filter(step => !s.excludePaths.some(path => stepMatchesPath(step, path)));
  const scopedVariants = safeArray(variants).filter(v => !variantSet.has(String(v?.variant_id ?? v?.id ?? v?.Variant ?? v?.variant)));

  const notes = [];
  if (variantSet.size) notes.push(`${variantSet.size} variant${variantSet.size === 1 ? '' : 's'} excluded`);
  if (workItemSet.size) notes.push(`${workItemSet.size} work item${workItemSet.size === 1 ? '' : 's'} excluded`);
  if (s.excludeFieldValues.length) notes.push(`${s.excludeFieldValues.length} field-value rule${s.excludeFieldValues.length === 1 ? '' : 's'} applied`);
  if (s.excludePaths.length) notes.push(`${s.excludePaths.length} path rule${s.excludePaths.length === 1 ? '' : 's'} applied to transition evidence`);
  if (filteredItemsBeforeSampleLimit > sampleItemLimit) notes.push(`sample limited to ${sampleItemLimit} of ${filteredItemsBeforeSampleLimit} matching work items`);
  if (s.instructions?.trim()) notes.push('additional report instructions supplied');

  return {
    rawSampleItems: scopedItems,
    processSteps: scopedSteps,
    variants: scopedVariants,
    summary: {
      enabled: true,
      originalItems,
      scopedItems: scopedItems.length,
      filteredItemsBeforeSampleLimit,
      sampleItemLimit,
      sampleLimitedItems: Math.max(0, filteredItemsBeforeSampleLimit - scopedItems.length),
      excludedItems: Math.max(0, originalItems - filteredItemsBeforeSampleLimit),
      originalSteps: safeArray(processSteps).length,
      scopedSteps: scopedSteps.length,
      excludedSteps: Math.max(0, safeArray(processSteps).length - scopedSteps.length),
      originalVariants: safeArray(variants).length,
      scopedVariants: scopedVariants.length,
      excludedVariantsFromEvidence: Math.max(0, safeArray(variants).length - scopedVariants.length),
      filtersApplied: variantSet.size + workItemSet.size + s.excludeFieldValues.length + s.excludePaths.length,
      notes,
      instructions: s.instructions || '',
      scope: s,
    },
  };
};

export const buildReportScopePromptSection = (scopeSummary = {}) => {
  if (!scopeSummary?.enabled) return '';
  const lines = [];
  if (scopeSummary.filtersApplied > 0) {
    lines.push(`Evidence filters were applied before generating this report.`);
    lines.push(`Sample work items sent after scope filters and sample limit: ${scopeSummary.scopedItems} of ${scopeSummary.originalItems}.`);
    if (scopeSummary.excludedSteps > 0) lines.push(`Transition evidence rows excluded by path filters: ${scopeSummary.excludedSteps}.`);
    if (scopeSummary.excludedVariantsFromEvidence > 0) lines.push(`Variant evidence rows excluded: ${scopeSummary.excludedVariantsFromEvidence}.`);
  }
  if (scopeSummary.sampleLimitedItems > 0) {
    if (scopeSummary.filtersApplied === 0) lines.push(`Sample work items sent after sample limit: ${scopeSummary.scopedItems} of ${scopeSummary.originalItems}.`);
    lines.push(`Sample item limit applied: ${scopeSummary.sampleItemLimit}; ${scopeSummary.sampleLimitedItems} additional matching items were not sent.`);
  }
  if (scopeSummary.notes?.length) lines.push(`Scope notes: ${scopeSummary.notes.join('; ')}.`);
  if (scopeSummary.instructions?.trim()) {
    lines.push('Additional user instructions for this report:');
    lines.push(scopeSummary.instructions.trim());
  }
  return lines.length ? `\n\nAI REPORT SCOPE\n${lines.join('\n')}\n` : '';
};
